import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { 
  TrendingUp, TrendingDown, Minus, Trophy, Target, Users,
  ArrowLeft, BarChart3, Activity, Award, Flame, Loader2
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Simple line chart component
const TrendChart = ({ data, label }) => {
  if (!data || data.length === 0) return <p className="text-muted-foreground text-sm">No data available</p>;
  
  const max = Math.max(...data.map(d => d.value), 1);
  const min = Math.min(...data.map(d => d.value), 0);
  const range = max - min || 1;
  
  return (
    <div className="h-32 flex items-end gap-1">
      {data.map((d, i) => {
        const height = ((d.value - min) / range) * 100;
        const color = d.result === 'W' ? 'bg-green-500' : d.result === 'L' ? 'bg-red-500' : 'bg-yellow-500';
        return (
          <div key={i} className="flex-1 flex flex-col items-center gap-1">
            <div 
              className={`w-full ${color} rounded-t transition-all`}
              style={{ height: `${Math.max(height, 5)}%` }}
              title={`${d.opponent}: ${d.value} pts (${d.result})`}
            />
            <span className="text-[10px] text-muted-foreground truncate w-full text-center">
              {d.result}
            </span>
          </div>
        );
      })}
    </div>
  );
};

// Stat card component
const StatCard = ({ title, value, subtitle, icon: Icon, trend, color = "primary" }) => (
  <Card className="overflow-hidden">
    <CardContent className="p-4">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-muted-foreground">{title}</p>
          <p className="text-2xl font-bold mt-1">{value}</p>
          {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
        </div>
        <div className={`w-10 h-10 rounded-lg bg-${color}/10 flex items-center justify-center`}>
          <Icon className={`w-5 h-5 text-${color}`} />
        </div>
      </div>
      {trend !== undefined && (
        <div className="flex items-center gap-1 mt-2">
          {trend > 0 ? (
            <TrendingUp className="w-4 h-4 text-green-500" />
          ) : trend < 0 ? (
            <TrendingDown className="w-4 h-4 text-red-500" />
          ) : (
            <Minus className="w-4 h-4 text-yellow-500" />
          )}
          <span className={`text-xs ${trend > 0 ? 'text-green-500' : trend < 0 ? 'text-red-500' : 'text-yellow-500'}`}>
            {trend > 0 ? '+' : ''}{trend}% vs last 5
          </span>
        </div>
      )}
    </CardContent>
  </Card>
);

// Player stats table
const PlayerStatsTable = ({ players }) => {
  if (!players || players.length === 0) {
    return <p className="text-muted-foreground text-center py-8">No player statistics available</p>;
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b">
            <th className="text-left py-3 px-2 font-medium">#</th>
            <th className="text-left py-3 px-2 font-medium">Player</th>
            <th className="text-center py-3 px-2 font-medium">GP</th>
            <th className="text-center py-3 px-2 font-medium">TD</th>
            <th className="text-center py-3 px-2 font-medium">INT</th>
            <th className="text-center py-3 px-2 font-medium">Pass Yds</th>
            <th className="text-center py-3 px-2 font-medium">Rush Yds</th>
            <th className="text-center py-3 px-2 font-medium">Rec Yds</th>
          </tr>
        </thead>
        <tbody>
          {players.map((player, i) => (
            <tr key={player.player_id} className="border-b hover:bg-muted/50">
              <td className="py-3 px-2 font-medium">{player.jersey_number || '-'}</td>
              <td className="py-3 px-2">
                <div>
                  <p className="font-medium">{player.name}</p>
                  <p className="text-xs text-muted-foreground">{player.position || 'N/A'}</p>
                </div>
              </td>
              <td className="text-center py-3 px-2">{player.games_played}</td>
              <td className="text-center py-3 px-2 font-bold text-green-500">{player.touchdowns}</td>
              <td className="text-center py-3 px-2">{player.interceptions}</td>
              <td className="text-center py-3 px-2">{player.passing_yards}</td>
              <td className="text-center py-3 px-2">{player.rushing_yards}</td>
              <td className="text-center py-3 px-2">{player.receiving_yards}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export const TeamAnalytics = () => {
  const { teamId } = useParams();
  const { token } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [playerStats, setPlayerStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchData();
  }, [teamId, token]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [analyticsRes, statsRes] = await Promise.all([
        axios.get(`${API}/teams/${teamId}/analytics`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/teams/${teamId}/player-stats`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setAnalytics(analyticsRes.data);
      setPlayerStats(statsRes.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background p-6">
        <Card className="max-w-md mx-auto">
          <CardContent className="p-6 text-center">
            <p className="text-red-500 mb-4">{error}</p>
            <Link to="/teams">
              <Button variant="outline">Back to Teams</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  const record = analytics?.record || {};
  const scoring = analytics?.scoring || {};
  const trends = analytics?.trends || {};
  const streak = analytics?.current_streak || {};
  const gameHistory = analytics?.game_history || [];

  // Prepare chart data
  const chartData = gameHistory.slice(-10).map(g => ({
    value: g.points_scored,
    result: g.result,
    opponent: g.opponent
  }));

  const trendDiff = trends.last_5_win_pct - trends.all_time_win_pct;

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto p-6">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Link to={`/teams/${teamId}`}>
            <Button variant="ghost" size="icon">
              <ArrowLeft className="w-5 h-5" />
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-bold">{analytics?.team_name}</h1>
            <p className="text-muted-foreground">Team Analytics & Performance</p>
          </div>
        </div>

        {/* Overview Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <StatCard 
            title="Record" 
            value={`${record.wins}-${record.losses}${record.ties > 0 ? `-${record.ties}` : ''}`}
            subtitle={`${record.win_percentage}% win rate`}
            icon={Trophy}
          />
          <StatCard 
            title="Points/Game" 
            value={scoring.avg_points_scored?.toFixed(1) || '0'}
            subtitle={`${scoring.avg_points_allowed?.toFixed(1) || '0'} allowed`}
            icon={Target}
          />
          <StatCard 
            title="Point Diff" 
            value={scoring.point_differential > 0 ? `+${scoring.point_differential}` : scoring.point_differential}
            subtitle={`${scoring.total_points_scored} scored total`}
            icon={BarChart3}
            trend={trendDiff}
          />
          <Card className="overflow-hidden">
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground">Current Streak</p>
              <div className="flex items-center gap-2 mt-1">
                <Flame className={`w-6 h-6 ${streak.type === 'W' ? 'text-green-500' : streak.type === 'L' ? 'text-red-500' : 'text-yellow-500'}`} />
                <span className="text-2xl font-bold">
                  {streak.count || 0}{streak.type || '-'}
                </span>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {streak.type === 'W' ? 'Win streak' : streak.type === 'L' ? 'Loss streak' : 'No streak'}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Tabs for detailed stats */}
        <Tabs defaultValue="performance" className="space-y-4">
          <TabsList>
            <TabsTrigger value="performance" className="gap-2">
              <Activity className="w-4 h-4" />
              Performance
            </TabsTrigger>
            <TabsTrigger value="players" className="gap-2">
              <Users className="w-4 h-4" />
              Player Stats
            </TabsTrigger>
            <TabsTrigger value="history" className="gap-2">
              <BarChart3 className="w-4 h-4" />
              Game History
            </TabsTrigger>
          </TabsList>

          <TabsContent value="performance">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Scoring Trend */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Scoring Trend (Last 10 Games)</CardTitle>
                </CardHeader>
                <CardContent>
                  <TrendChart data={chartData} label="Points Scored" />
                  <div className="flex justify-between mt-4 text-sm">
                    <div>
                      <p className="text-muted-foreground">Highest</p>
                      <p className="font-bold">{Math.max(...chartData.map(d => d.value), 0)} pts</p>
                    </div>
                    <div className="text-right">
                      <p className="text-muted-foreground">Lowest</p>
                      <p className="font-bold">{Math.min(...chartData.map(d => d.value), 0)} pts</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Win Percentage Trends */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Win Percentage Trends</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span>Last 5 Games</span>
                      <span className="font-bold">{trends.last_5_win_pct}%</span>
                    </div>
                    <div className="h-2 bg-muted rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-primary transition-all"
                        style={{ width: `${trends.last_5_win_pct}%` }}
                      />
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span>Last 10 Games</span>
                      <span className="font-bold">{trends.last_10_win_pct}%</span>
                    </div>
                    <div className="h-2 bg-muted rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-blue-500 transition-all"
                        style={{ width: `${trends.last_10_win_pct}%` }}
                      />
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span>All Time</span>
                      <span className="font-bold">{trends.all_time_win_pct}%</span>
                    </div>
                    <div className="h-2 bg-muted rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-purple-500 transition-all"
                        style={{ width: `${trends.all_time_win_pct}%` }}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="players">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Player Statistics</CardTitle>
              </CardHeader>
              <CardContent>
                <PlayerStatsTable players={playerStats?.players || []} />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="history">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Recent Games</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {gameHistory.length === 0 ? (
                    <p className="text-muted-foreground text-center py-8">No games played yet</p>
                  ) : (
                    gameHistory.slice().reverse().map((game, i) => (
                      <div 
                        key={game.game_id || i}
                        className="flex items-center justify-between p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <Badge 
                            variant={game.result === 'W' ? 'default' : game.result === 'L' ? 'destructive' : 'secondary'}
                            className="w-8 justify-center"
                          >
                            {game.result}
                          </Badge>
                          <div>
                            <p className="font-medium">vs {game.opponent}</p>
                            <p className="text-xs text-muted-foreground">
                              {game.date ? new Date(game.date).toLocaleDateString() : 'Date TBD'}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-lg">{game.score}</p>
                          <p className="text-xs text-muted-foreground">
                            {game.result === 'W' ? `+${game.points_scored - game.points_allowed}` : game.points_scored - game.points_allowed}
                          </p>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default TeamAnalytics;
